import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Download, Upload } from 'lucide-react';
import { useAuth } from '@/core/auth';
import { resolveError } from '@/core/errors/resolveError';
import { ManagedDataTable } from '@/design-system';
import type { ToolbarButton } from '@/design-system/ManagedDataTable';
import {
  type WhatsAppPricing,
  type WhatsAppPricingCategory,
} from '../../api/whatsapp-pricing.service';
import { useWhatsAppPricing } from '../../hooks/useWhatsAppPricing';

const CATEGORIES: Array<{ id: WhatsAppPricingCategory; label: string; csv: string }> = [
  { id: 'marketing', label: 'Marketing', csv: 'Marketing' },
  { id: 'utility', label: 'Utilidade', csv: 'Utility' },
  { id: 'authentication', label: 'Autenticação', csv: 'Authentication' },
];

interface PricingRow {
  id: string;
  name: string;
  rate: Record<WhatsAppPricingCategory, number>;
}

const brl = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

function marketName(id: string): string {
  return id
    .split('_')
    .map((word, i) => (i > 0 && word === 'of' ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
}

function downloadCsv(pricing: WhatsAppPricing) {
  const effective = new Date(`${pricing.effectiveFrom}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const lines = [
    `Cost per message in BRL on the WhatsApp Business Platform, effective ${effective}`,
    ['Market', 'Currency', ...CATEGORIES.map((c) => c.csv)].join(','),
    ...Object.entries(pricing.rates).map(([id, rate]) =>
      [marketName(id), 'BRL', ...CATEGORIES.map((c) => rate[c.id])].join(',')
    ),
  ];
  const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `precos-whatsapp-${pricing.effectiveFrom}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function WhatsAppPricingSection() {
  const { user } = useAuth();
  const fileInput = useRef<HTMLInputElement>(null);
  const { query, importPricing } = useWhatsAppPricing();
  const pricing = query.data;
  const [search, setSearch] = useState('');
  const importing = importPricing.isPending;
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);


  const rows = useMemo(() => {
    if (!pricing) return [];
    return Object.entries(pricing.rates)
      .map(([id, rate]) => ({ id, name: marketName(id), rate }))
      .sort((a, b) => (a.id === 'brazil' ? -1 : b.id === 'brazil' ? 1 : a.name.localeCompare(b.name)));
  }, [pricing]);

  const columns = useMemo<ColumnDef<PricingRow>[]>(() => [
    { accessorKey: 'name', header: 'Mercado', minSize: 200 },
    ...CATEGORIES.map((category): ColumnDef<PricingRow> => ({
      id: category.id,
      accessorFn: (row) => row.rate[category.id],
      header: category.label,
      cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{brl.format(row.original.rate[category.id])}</span>,
      enableGlobalFilter: false,
    })),
  ], []);

  async function handleImport(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAlert(null);
    try {
      await importPricing.mutateAsync(file);
      setAlert({ type: 'success', msg: 'Tabela importada. Os novos preços valem em até 1 minuto.' });
    } catch (err) {
      setAlert({ type: 'error', msg: resolveError(err).message });
    }
  }

  if (!pricing) {
    return query.isError ? <p role="alert" className="text-sm text-red-700">{resolveError(query.error).message}</p> : <p className="text-sm text-gray-500">Carregando…</p>;
  }

  return (
    <div className="space-y-4">
      {alert && (
        <p
          className={`rounded-lg px-4 py-3 text-sm ${
            alert.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {alert.msg}
        </p>
      )}

      <ManagedDataTable
        title="Preços WhatsApp"
        subtitle={<>Tabela oficial da Meta, em BRL por mensagem. Vigente desde{' '}
          <strong className="font-semibold text-gray-900">{new Date(`${pricing.effectiveFrom}T00:00:00Z`).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}</strong>.
        </>}
        columns={columns}
        data={rows}
        searchPlaceholder="Buscar mercado..."
        searchValue={search}
        onSearchChange={setSearch}
        getRowId={(row) => row.id}
        stickyHeader
        maxHeight="60vh"
        toolbarButtons={[
          { label: 'Exportar CSV', icon: <Download size={14} />, variant: 'subtle', onClick: () => downloadCsv(pricing) },
          ...(user?.role === 'SUPERADMIN' ? [{
            label: importing ? 'Importando...' : 'Importar planilha da Meta',
            icon: <Upload size={14} />,
            variant: 'primary',
            disabled: importing,
            onClick: () => fileInput.current?.click(),
          } satisfies ToolbarButton] : []),
        ]}
        emptyMessage="Nenhum mercado encontrado."
      />
      {user?.role === 'SUPERADMIN' && <input
        ref={fileInput}
        type="file"
        accept=".csv,.xlsx"
        className="hidden"
        onChange={handleImport}
      />}
    </div>
  );
}
